import axios from 'axios';

import firebase from './firebase';
import { buildApiUrl } from './api';
import { activeShopHeaders, getActiveShopId } from './shopContext';

const QUEUE_KEY = 'autoclever.errorTelemetryQueue.v1';
const MAX_QUEUE_SIZE = 50;
const MAX_CONTEXT_DEPTH = 4;
const installedAxiosClients = new WeakSet();
const reportedErrors = new WeakSet();
const originalConsoleError = console.error.bind(console);
let initialized = false;
let flushing = false;
let retryTimer = null;

const isCancellation = (error) => (
  axios.isCancel(error) ||
  error?.code === 'ERR_CANCELED' ||
  error?.name === 'AbortError' ||
  /(?:aborted|cancelled|canceled)/i.test(String(error?.message || ''))
);

const isExpectedBrowserNoise = (error) => (
  isCancellation(error) ||
  /ResizeObserver loop/i.test(String(error?.message || error || ''))
);

const redactText = (value, max = 30000) => String(value ?? '')
  .trim()
  .slice(0, max)
  .replace(/\bBearer\s+[A-Za-z0-9._~+/=-]+/gi, 'Bearer [REDACTED]')
  .replace(/\b[A-Za-z0-9_-]{16,}\.[A-Za-z0-9_-]{16,}\.[A-Za-z0-9_-]{16,}\b/g, '[REDACTED_JWT]')
  .replace(/([?&](?:token|key|secret|password|code)=)[^&\s]+/gi, '$1[REDACTED]')
  .replace(/\b(password|passcode|authorization|cookie|credential|[a-z0-9_-]*(?:token|secret|key))\s*[:=]\s*[^\s,;]+/gi, '$1=[REDACTED]')
  .replace(/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi, '[REDACTED_EMAIL]')
  .replace(/(?:\+?\d[\s().-]*){10,15}/g, '[REDACTED_PHONE]');

const sensitiveKey = (key) => /(?:password|passcode|authorization|cookie|token|secret|api[_-]?key|private[_-]?key|credential)/i.test(key);

const sanitizeValue = (value, depth = 0) => {
  if (depth > MAX_CONTEXT_DEPTH || value === null || value === undefined) return null;
  if (typeof value === 'string') return redactText(value, 2000);
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'boolean') return value;
  if (value instanceof Error) {
    return { name: value.name || 'Error', message: redactText(value.message, 4000) };
  }
  if (Array.isArray(value)) return value.slice(0, 20).map((entry) => sanitizeValue(entry, depth + 1));
  if (typeof value === 'object') {
    return Object.entries(value)
      .slice(0, 50)
      .reduce((result, [key, entry]) => ({
        ...result,
        [String(key).slice(0, 128)]: sensitiveKey(key)
          ? '[REDACTED]'
          : sanitizeValue(entry, depth + 1)
      }), {});
  }
  return redactText(value, 1000);
};

const safeRoute = (value) => {
  try {
    return new URL(String(value || ''), window.location.origin).pathname.slice(0, 1024);
  } catch (_error) {
    return window.location.pathname.slice(0, 1024);
  }
};

const eventId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `web-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
};

const loadQueue = () => {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(QUEUE_KEY) || '[]');
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((item) => {
        if (item?.payload?.eventId && Object.prototype.hasOwnProperty.call(item, 'authScope')) {
          return item;
        }
        // Preserve queued events from the pre-scoped format without ever
        // attributing them to whichever account happens to sign in next.
        return item?.eventId ? { authScope: null, payload: item } : null;
      })
      .filter(Boolean)
      .slice(-MAX_QUEUE_SIZE);
  } catch (_error) {
    return [];
  }
};

const saveQueue = (events) => {
  try {
    window.localStorage.setItem(QUEUE_KEY, JSON.stringify(events.slice(-MAX_QUEUE_SIZE)));
  } catch (_error) {
    // Storage can be unavailable in private browsing; telemetry must never break the app.
  }
};

const currentAuthScope = () => {
  const userId = firebase.auth().currentUser?.uid;
  if (!userId) return null;
  return `${userId}:${getActiveShopId() || 'unscoped'}`;
};

const queueEvent = (event) => {
  const events = loadQueue();
  if (!events.some((item) => item.payload.eventId === event.eventId)) {
    events.push({ authScope: currentAuthScope(), payload: event });
  }
  saveQueue(events);
};

const authHeaders = async (authenticated) => {
  if (!authenticated) return {};
  const user = firebase.auth().currentUser;
  if (!user) throw new Error('Telemetry session is not available.');
  const token = await user.getIdToken();
  return { ...activeShopHeaders(), Authorization: `Bearer ${token}` };
};

const postEvents = async (events, authenticated) => {
  const response = await window.fetch(buildApiUrl('/api/telemetry/errors'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(await authHeaders(authenticated))
    },
    body: JSON.stringify(events.length === 1
      ? events[0].payload
      : { events: events.map((event) => event.payload) })
  });
  if (response.ok) return true;
  if (response.status === 429 || response.status >= 500) {
    const error = new Error(`Telemetry endpoint returned ${response.status}`);
    const retryAfter = Number(response.headers.get('retry-after') || 0);
    error.retryAfterMs = retryAfter > 0 ? retryAfter * 1000 : 30000;
    throw error;
  }
  return false;
};

const scheduleRetry = (delayMs = 30000) => {
  if (retryTimer) return;
  retryTimer = window.setTimeout(() => {
    retryTimer = null;
    flushWebErrorTelemetry();
  }, Math.max(1000, Math.min(10 * 60 * 1000, Number(delayMs) || 30000)));
};

export const flushWebErrorTelemetry = async () => {
  if (flushing || !window.navigator.onLine) return;
  const events = loadQueue();
  if (!events.length) return;
  flushing = true;
  try {
    const scope = currentAuthScope();
    const scopedEvents = scope ? events.filter((event) => event.authScope === scope) : [];
    const authenticated = scopedEvents.length > 0;
    const batch = (authenticated
      ? scopedEvents
      : events.filter((event) => event.authScope === null))
      .slice(0, 10);
    if (!batch.length) return;
    await postEvents(batch, authenticated);
    const sentIds = new Set(batch.map((event) => event.payload.eventId));
    // A non-retryable 4xx means the payload is invalid and must not poison the
    // queue forever. Successful and non-retryable batches are both removed.
    saveQueue(events.filter((item) => !sentIds.has(item.payload.eventId)));
  } catch (error) {
    scheduleRetry(error?.retryAfterMs);
  } finally {
    flushing = false;
    const scope = currentAuthScope();
    const hasEligibleEvents = loadQueue().some((event) => (
      event.authScope === null || event.authScope === scope
    ));
    if (hasEligibleEvents && !retryTimer && window.navigator.onLine) {
      scheduleRetry(250);
    }
  }
};

const normalizeError = (error, fallbackMessage) => {
  if (error instanceof Error) return error;
  if (error && typeof error === 'object' && typeof error.message === 'string') {
    const normalized = new Error(error.message);
    normalized.name = String(error.name || 'Error');
    normalized.stack = error.stack || normalized.stack;
    return normalized;
  }
  return new Error(fallbackMessage || String(error || 'Unknown browser error'));
};

const browserRelease = () => {
  const configured = process.env.REACT_APP_RELEASE || process.env.REACT_APP_VERSION;
  if (configured) return configured;
  const mainBundle = Array.from(document.scripts || [])
    .map((script) => String(script.src || ''))
    .find((source) => /\/static\/js\/main\.[^.]+\.js(?:$|\?)/.test(source));
  const hash = mainBundle?.match(/\/static\/js\/main\.([^.]+)\.js(?:$|\?)/)?.[1];
  return hash ? `web:${hash}` : 'autoclever-web';
};

export const reportWebError = (error, details = {}) => {
  const normalized = normalizeError(error, details.message);
  if (isExpectedBrowserNoise(normalized)) return;
  if (error instanceof Error) {
    if (reportedErrors.has(error)) return;
    reportedErrors.add(error);
  }
  const payload = {
    eventId: details.eventId || eventId(),
    app: 'autoclever_frontend',
    environment: process.env.NODE_ENV || 'production',
    severity: details.severity || 'error',
    source: details.source || 'runtime',
    errorType: details.errorType || normalized.name || 'Error',
    message: redactText(details.message || normalized.message || 'Unknown browser error', 12000),
    stack: redactText(details.stack || normalized.stack, 30000) || null,
    sourceFile: redactText(details.sourceFile, 1024) || null,
    sourceLine: Number(details.sourceLine || 0) || null,
    sourceColumn: Number(details.sourceColumn || 0) || null,
    route: safeRoute(details.route || window.location.href),
    release: browserRelease(),
    platform: `${window.navigator.userAgent}`.slice(0, 128),
    occurredAt: new Date().toISOString(),
    context: sanitizeValue({
      visibilityState: document.visibilityState,
      online: window.navigator.onLine,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      ...details.context
    })
  };

  queueEvent(payload);
  flushWebErrorTelemetry();
};

const axiosPath = (error) => {
  const url = error?.config?.url;
  return url ? safeRoute(url) : window.location.pathname;
};

const retryableReadFailure = (error) => {
  const config = error?.config;
  const method = String(config?.method || 'GET').toUpperCase();
  const status = Number(error?.response?.status || 0);
  if (
    !config ||
    config.autocleverDisableRetry ||
    isCancellation(error) ||
    !['GET', 'HEAD'].includes(method)
  ) return false;
  if (status === 0) {
    return window.navigator.onLine && document.visibilityState === 'visible';
  }
  return status === 429 || status === 502 || status === 503 || status === 504;
};

const retryReadRequest = async (client, error) => {
  const config = error.config;
  const attempt = Number(config.autocleverRetryAttempt || 0);
  if (!retryableReadFailure(error) || attempt >= 2) return null;
  config.autocleverRetryAttempt = attempt + 1;
  const retryAfterSeconds = Number(error?.response?.headers?.['retry-after'] || 0);
  const delayMs = retryAfterSeconds > 0
    ? Math.min(5000, retryAfterSeconds * 1000)
    : 250 * (2 ** attempt) + Math.floor(Math.random() * 100);
  await new Promise((resolve) => window.setTimeout(resolve, delayMs));
  return client.request(config);
};

export const installAxiosErrorTelemetry = (client) => {
  if (!client?.interceptors?.response || installedAxiosClients.has(client)) return;
  installedAxiosClients.add(client);
  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const retriedResponse = await retryReadRequest(client, error);
      if (retriedResponse) return retriedResponse;
      const status = Number(error?.response?.status || 0);
      const path = axiosPath(error);
      const expectedLifecycleResponse = (
        isCancellation(error) ||
        ((status === 401 || status === 403) && /\/api\/(?:auth|mobile)\//.test(path)) ||
        (status >= 400 && status < 500 && error?.config?.telemetryOptional === true) ||
        (status === 0 && (!window.navigator.onLine || document.visibilityState === 'hidden'))
      );
      if (!path.includes('/api/telemetry/errors') && !expectedLifecycleResponse) {
        reportWebError(error, {
          source: 'http',
          severity: status >= 500 || status === 0 ? 'error' : 'warning',
          errorType: status ? `HTTP${status}` : 'NetworkError',
          route: path,
          context: {
            method: String(error?.config?.method || 'GET').toUpperCase(),
            status,
            code: error?.code || null
          }
        });
      }
      return Promise.reject(error);
    }
  );
};

const errorFromConsole = (args) => {
  const error = args.find((entry) => entry instanceof Error);
  return error || null;
};

export const initializeWebErrorTelemetry = () => {
  if (initialized) return;
  initialized = true;
  installAxiosErrorTelemetry(axios);

  window.addEventListener('error', (event) => {
    const target = event.target;
    const resourceUrl = target && target !== window
      ? target.src || target.href || null
      : null;
    reportWebError(event.error || new Error(event.message || `Resource failed to load: ${resourceUrl || 'unknown'}`), {
      source: resourceUrl ? 'resource' : 'window',
      sourceFile: event.filename || resourceUrl,
      sourceLine: event.lineno,
      sourceColumn: event.colno,
      context: resourceUrl ? { resourcePath: safeRoute(resourceUrl) } : null
    });
  }, true);

  window.addEventListener('unhandledrejection', (event) => {
    reportWebError(event.reason, { source: 'unhandled_rejection' });
  });

  console.error = (...args) => {
    originalConsoleError(...args);
    const error = errorFromConsole(args);
    if (error) {
      reportWebError(error, { source: 'console' });
    }
  };

  window.addEventListener('online', () => {
    flushWebErrorTelemetry();
  });
  window.setTimeout(() => {
    flushWebErrorTelemetry();
  }, 1500);
};
